import React, { useState } from 'react';
import { useNovelUpdate } from '../hooks/useNovelUpdate';
import Conditionals from './Conditionals';
import { getAllEndingsFromNovel } from '../utils/endingsHelper';
import './WorldPromptsCategory.css';


const MAX_TOTAL_WORLD_PROMPTS_LENGTH = 15000;

const WorldPromptsCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [newPromptConditionals, setNewPromptConditionals] = useState([]);
  const [newDescription, setNewDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use data from novel prop instead of local state
  const worldPrompts = novel?.world_prompts || [];
  const flags = novel?.states?.flags || [];
  const scales = novel?.states?.scales || [];

  const { updateNovel } = useNovelUpdate(novelId, isProduction, refreshNovel);

  // Calculate total length of all world prompt descriptions
  const totalWorldPromptsLength = worldPrompts.reduce(
    (sum, p) => sum + (p.prompt_description || '').length, 0
  );

  // For create form: remaining chars available
  const remainingForNew = MAX_TOTAL_WORLD_PROMPTS_LENGTH - totalWorldPromptsLength;

  // For edit form: remaining chars available (excluding the prompt being edited from the total)
  const originalEditLength = editingPrompt
    ? (worldPrompts.find(p => p.id === editingPrompt.id)?.prompt_description || '').length
    : 0;
  const remainingForEdit = MAX_TOTAL_WORLD_PROMPTS_LENGTH - totalWorldPromptsLength + originalEditLength;

  const handleCreatePrompt = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const description = formData.get('description')?.trim();

    if (!name) {
      setError('Название промпта обязательно');
      setLoading(false);
      return;
    }

    if (!description) {
      setError('Описание промпта обязательно');
      setLoading(false);
      return;
    }

    try {
      const newPrompt = {
        name,
        prompt_description: description,
        conditionals: newPromptConditionals
      };

      const updatedWorldPrompts = [...worldPrompts, newPrompt];

      const updatedNovel = {
        ...novel,
        world_prompts: updatedWorldPrompts
      };

      await updateNovel(updatedNovel);
      setShowCreateForm(false);
      setNewPromptConditionals([]);
      setNewDescription('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrompt = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    const description = formData.get('description')?.trim();

    if (!name) {
      setError('Название промпта обязательно');
      setLoading(false);
      return;
    }

    if (!description) {
      setError('Описание промпта обязательно');
      setLoading(false);
      return;
    }

    try {
      const updatedPrompt = {
        ...editingPrompt,
        name,
        prompt_description: description
      };

      const updatedWorldPrompts = worldPrompts.map(prompt =>
        prompt.id === editingPrompt.id ? updatedPrompt : prompt
      );

      const updatedNovel = {
        ...novel,
        world_prompts: updatedWorldPrompts
      };

      await updateNovel(updatedNovel);
      setEditingPrompt(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот промпт мира?')) {
      return;
    }

    try {
      setLoading(true);
      const updatedWorldPrompts = worldPrompts.filter(prompt => prompt.id !== promptId);

      const updatedNovel = {
        ...novel,
        world_prompts: updatedWorldPrompts
      };

      await updateNovel(updatedNovel);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConditionalsChange = (newConditionals) => {
    if (editingPrompt) {
      setEditingPrompt({
        ...editingPrompt,
        conditionals: newConditionals
      });
    }
  };

  const handleNewPromptConditionalsChange = (newConditionals) => {
    setNewPromptConditionals(newConditionals);
  };

  return (
    <div className="wp-wrap">
      {/* Description */}
      <div className="wp-desc">
        <p>Промпты мира предоставляют контекстные описания, которые влияют на ответы персонажей при выполнении их условий.</p>
        <div className={`wp-total-counter${totalWorldPromptsLength >= MAX_TOTAL_WORLD_PROMPTS_LENGTH ? ' wp-total-counter--limit' : ''}`}>
          Общий объём промптов: {totalWorldPromptsLength} / {MAX_TOTAL_WORLD_PROMPTS_LENGTH} символов
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="wp-error">
          {error}
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <div className="wp-overlay">
          <div className="wp-modal">
            <h3 className="wp-modal-title">Добавить промпт мира</h3>
            <form onSubmit={handleCreatePrompt}>
              <div className="wp-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название промпта"
                  required
                  disabled={loading}
                  className="wp-input"
                />
              </div>

              <div className="wp-form-group">
                <textarea
                  name="description"
                  placeholder="Описание промпта"
                  required
                  disabled={loading}
                  className="wp-textarea"
                  rows={4}
                  value={newDescription}
                  maxLength={remainingForNew > 0 ? remainingForNew : 0}
                  onChange={(e) => {
                    if (e.target.value.length <= remainingForNew) {
                      setNewDescription(e.target.value);
                    }
                  }}
                />
                <div className={`wp-char-counter${remainingForNew - newDescription.length <= 0 ? ' wp-char-counter--limit' : ''}`}>
                  {totalWorldPromptsLength + newDescription.length} / {MAX_TOTAL_WORLD_PROMPTS_LENGTH} символов всего
                </div>
              </div>

              <div className="conditionals-section">
                <h4>Когда активировать этот промпт:</h4>
                <Conditionals
                  flags={flags}
                  scales={scales}
                  endings={getAllEndingsFromNovel(novel)}
                  value={newPromptConditionals}
                  onChange={handleNewPromptConditionalsChange}
                  disabled={loading}
                />
              </div>

              <div className="wp-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="wp-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewPromptConditionals([]);
                    setNewDescription('');
                  }}
                  disabled={loading}
                  className="wp-btn-cancel"
                >
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingPrompt && (
        <div className="wp-overlay">
          <div className="wp-modal">
            <h3 className="wp-modal-title">Редактировать промпт мира</h3>
            <form onSubmit={handleEditPrompt}>
              <div className="wp-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название промпта"
                  defaultValue={editingPrompt.name}
                  required
                  disabled={loading}
                  className="wp-input"
                />
              </div>

              <div className="wp-form-group">
                <textarea
                  name="description"
                  placeholder="Описание промпта"
                  value={editingPrompt.prompt_description}
                  required
                  disabled={loading}
                  className="wp-textarea"
                  rows={4}
                  maxLength={remainingForEdit > 0 ? remainingForEdit : 0}
                  onChange={(e) => {
                    if (e.target.value.length <= remainingForEdit) {
                      setEditingPrompt({ ...editingPrompt, prompt_description: e.target.value });
                    }
                  }}
                />
                <div className={`wp-char-counter${remainingForEdit - (editingPrompt.prompt_description || '').length <= 0 ? ' wp-char-counter--limit' : ''}`}>
                  {totalWorldPromptsLength - originalEditLength + (editingPrompt.prompt_description || '').length} / {MAX_TOTAL_WORLD_PROMPTS_LENGTH} символов всего
                </div>
              </div>

              <div className="conditionals-section">
                <h4>Когда активировать этот промпт:</h4>
                <Conditionals
                  flags={flags}
                  scales={scales}
                  endings={getAllEndingsFromNovel(novel)}
                  value={editingPrompt.conditionals || []}
                  onChange={handleConditionalsChange}
                  disabled={loading}
                />
              </div>

              <div className="wp-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="wp-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingPrompt(null)}
                  disabled={loading}
                  className="wp-btn-cancel"
                >
                  Отменить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Controls */}
      <button
        className="wp-add-btn"
        onClick={() => { setShowCreateForm(true); setNewDescription(''); }}
      >
        <span>➕</span>
        Добавить промпт мира
      </button>

      {/* World Prompts List */}
      <div className="wp-list">
        {worldPrompts.length > 0 ? (
          worldPrompts.map((prompt) => (
            <div key={prompt.id} className="wp-card">
              <div className="wp-card-head">
                <h4 className="wp-card-name">{prompt.name}</h4>
                <div className="wp-card-actions">
                  <button
                    className="wp-btn-edit"
                    onClick={() => setEditingPrompt(prompt)}
                    disabled={loading}
                  >
                    Редактировать
                  </button>
                  <button
                    className="wp-btn-delete"
                    onClick={() => handleDeletePrompt(prompt.id)}
                    disabled={loading}
                  >
                    Удалить
                  </button>
                </div>
              </div>
              <div className="prompt-content">
                <p className="wp-card-desc">{prompt.prompt_description}</p>
                <div className="wp-card-conds">
                  <strong>Условия:</strong>
                  {prompt.conditionals && prompt.conditionals.length > 0 ? (
                    <span className="conditionals-count">{prompt.conditionals.length} OR групп</span>
                  ) : (
                    <span className="no-conditionals">Всегда активен</span>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="wp-empty">
            <p>Промптов мира пока нет. Создайте свой первый промпт мира!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorldPromptsCategory;
